import React from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import MuiButton from '@material-ui/core/Button'
import type { ButtonProps as MuiButtonProps } from '@material-ui/core/Button'
import classnames from 'classnames'

export type FileViewerButtonTone = 'neutral' | 'primary' | 'danger'

export interface FileViewerButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
  /**
   * Visual weight. 'neutral' (default) is the rounded outlined style used for
   * Edit / Change / Delete / Select all / Back to files. 'primary' is the filled
   * blue Save / Done style. 'danger' tints the outline red.
   */
  tone?: FileViewerButtonTone

  /**
   * When true, the button is shown as 'selected' (filled background, no border)
   * — used for the Show on Estimate / Work Order toggles.
   */
  selected?: boolean

  /**
   * Use for icon-only square buttons (toolbar in the editor: pen, arrow, crop, etc.)
   */
  iconOnly?: boolean
}

const useStyles = makeStyles<Theme, FileViewerButtonProps>((theme) => ({
  root: {
    textTransform: 'none',
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.pxToRem(13),
    letterSpacing: 0,
    borderRadius: theme.borderRadius.md,
    padding: '6px 12px',
    minWidth: 'auto',
    boxShadow: 'none',
    whiteSpace: 'nowrap',
    // The mobile "Upload a file" button opens the native picker; iPad lifts a
    // preview of the tapped element, so suppress it (#20634).
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2
    }
  },
  neutral: {
    background: theme.palette.common.white,
    color: theme.paintscout.palette.brand.Neutral.P30,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: theme.paintscout.palette.brand.Neutral.P96,
        borderColor: theme.paintscout.palette.brand.Neutral.P70
      }
    }
  },
  neutralSelected: {
    background: theme.paintscout.palette.brand.Neutral.P96,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`
  },
  primary: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: '1px solid transparent',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: theme.palette.primary.dark
      }
    },
    '&$disabled': {
      background: theme.paintscout.palette.brand.Neutral.P90,
      color: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  danger: {
    background: theme.palette.common.white,
    color: theme.palette.error.main,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        background: theme.palette.error.light,
        borderColor: theme.palette.error.main
      }
    }
  },
  iconOnly: {
    width: 32,
    height: 32,
    padding: 0,
    '& svg': {
      fontSize: 18
    }
  },
  disabled: {}
}))

const FileViewerButton = React.forwardRef<HTMLButtonElement, FileViewerButtonProps>(function FileViewerButton(
  { tone = 'neutral', selected, iconOnly, className, children, ...rest },
  ref
) {
  const classes = useStyles({ tone, selected, iconOnly, ...rest })

  const toneClass =
    tone === 'primary'
      ? classes.primary
      : tone === 'danger'
        ? classes.danger
        : selected
          ? classes.neutralSelected
          : classes.neutral

  return (
    <MuiButton
      ref={ref}
      disableRipple
      classes={{ disabled: classes.disabled }}
      className={classnames(classes.root, toneClass, iconOnly && classes.iconOnly, className)}
      {...rest}
    >
      {children}
    </MuiButton>
  )
})

export default FileViewerButton
